import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Download, Shield, ArrowLeft } from "lucide-react";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import { toast } from "@/hooks/use-toast";
import { generatePdfBlob, downloadPdf, openPdfInNewTab } from "@/lib/pdfUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TableRow {
  mistake: string;
  impact: string;
  fix: string;
}

interface BlogContentItem {
  title: string;
  description: string;
  references: string[];
}

interface BlogContentSection extends BlogContentItem {
  content?: BlogContentItem[];
  table?: TableRow[];
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  readTime: string;
  category: string;
  content?: BlogContentSection[];
}

interface ResourceItem {
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  type: string;
  size: string;
  category: string;
  pages?: string;
  items?: string;
  features?: string;
  tips?: string;
  content?: any;
}



const Resources = () => {
  const [selectedBlog, setSelectedBlog] = useState<BlogPost | null>(null);
  const [isBlogOpen, setIsBlogOpen] = useState(false);

  const downloadResources: ResourceItem[] = [
    {
      icon: FileText,
      title: "Financial Planning Handbook",
      description: "Comprehensive guide to financial planning, budgeting, investments, and wealth management strategies.",
      type: "PDF Guide",
      size: "2.8 MB",
      pages: "12 chapters",
      category: "Guide"
    },
    {
      icon: FileText,
      title: "Interest Rate Comparison Report",
      description: "Detailed comparison of interest rates across banks and NBFCs for all loan products, updated monthly.",
      type: "PDF Report",
      size: "1.5 MB",
      pages: "5 pages",
      category: "Report"
    },
    {
      icon: FileText,
      title: "Complete Loan Eligibility Guide",
      description: "Comprehensive guide covering eligibility criteria for all loan types including documents required, CIBIL score requirements, and income criteria.",
      type: "PDF Guide",
      size: "3.2 MB",
      pages: "45 pages",
      category: "Guide"
    },
    {
      icon: FileText,
      title: "Documentation Checklist",
      description: "Comprehensive checklist of all required documents for Personal, Home, Business, Car, and Education loans with detailed specifications.",
      type: "PDF Checklist",
      size: "2.1 MB",
      items: "50+ Documents",
      category: "Checklist",
      content: {
        personalLoan: [
          "Valid government-issued photo ID (e.g., passport, driver's license)",
          "Social Security/Tax ID",
          "Proof of income: recent pay stubs (1-2 months)",
          "W-2s or tax returns (1-2 years)",
          "1099s (for freelancers)",
          "Bank account statements (last 2-3 months)",
          "Proof of residence (utility bill, lease)",
          "Recent credit report pulled by lender",
          "Additional documentation: asset proof, letters for alimony/child support, employer verification"
        ],
        homeLoan: [
          "Photo ID & Social Security card",
          "Income verification: last 30 days' pay stubs",
          "W-2s, tax returns (2 years)",
          "Profit/loss statements for self-employed",
          "Employment history and VOE (last 2 years)",
          "Bank and asset statements (2-3 months)",
          "Credit report and credit history overview",
          "Debt documentation: credit cards, car loans, student loans",
          "Purchase contract and earnest money deposit proof",
          "Insurance documents: homeowner's, flood (if required)",
          "Special documents: divorce decree, child support documents"
        ],
        mortgage: [
          "Photo ID & Social Security card",
          "Income verification: last 30 days' pay stubs, W-2s (2 years)",
          "Tax returns (2 years) with all schedules",
          "Business tax returns (if self-employed)",
          "Bank statements (2-3 months for all accounts)",
          "Investment and retirement account statements",
          "Existing mortgage statements (if applicable)",
          "Proof of rental income (if applicable)",
          "Gift letter (if using gift funds)",
          "Bankruptcy/foreclosure documents (if applicable)",
          "VA/Government loan documents (if applicable)",
          "Homeowners insurance information",
          "Property tax information",
          "HOA documents (if applicable)",
          "Purchase agreement (for new purchases)",
          "Proof of down payment and closing costs"
        ],
        businessLoan: [
          "Business and personal tax returns (2+ years)",
          "Business profit & loss statements, balance sheet",
          "Bank statements for business accounts (2-3 months)",
          "Personal income proof: pay stubs, W-2s, 1099s",
          "Business and personal credit reports",
          "Business licenses and registration",
          "Operating agreements",
          "Asset documentation",
          "Debt schedules (leases, loans)",
          "Collateral papers"
        ],
        carLoan: [
          "Valid driver's license",
          "Proof of residence: utility bill, lease",
          "Income verification: pay stubs (30 days), W-2s or tax returns",
          "Bank statements (sometimes required)",
          "Vehicle information: VIN, sale bill/purchase agreement",
          "Credit report from lender",
          "Insurance proof (auto insurance)"
        ],
        educationLoan: [
          "ID & Social Security/Tax ID",
          "Income proof (applicant or cosigner): pay stubs, tax returns",
          "Bank statements",
          "School acceptance/enrollment letter",
          "Tuition bill or fee schedule",
          "Cost of attendance estimate",
          "Financial aid award letters",
          "Cosigner credit report and ID (if applicable)"
        ]
      }
    },
    {
      icon: Shield,
      title: "Credit Score Improvement Guide",
      description: "Step-by-step guide to improve your CIBIL score with actionable tips and real case studies.",
      type: "PDF Guide",
      size: "2.8 MB",
      tips: "25+ Tips",
      category: "Guide"
    }
  ];

  const handleBlogClick = (blogId: string) => {
    const blog = blogs.find(b => b.id === blogId);
    if (blog) {
      setSelectedBlog(blog);
      setIsBlogOpen(true);
    }
  };

  const closeBlog = () => {
    setIsBlogOpen(false);
    setTimeout(() => setSelectedBlog(null), 300); // Allow animation to complete
  };

  const blogs: BlogPost[] = [
    {
      id: 'personal-loan-mistakes',
      title: "10 Mistakes to Avoid for Faster Personal Loan Approval",
      excerpt: "Learn the critical mistakes that can delay or derail your personal loan application and how to avoid them.",
      readTime: "8 min read",
      category: "Personal Loans",
      content: [
        {
          title: "1. Borrowing More or Longer Than Needed",
          description: "Larger amounts or longer tenures increase total interest paid. Opt for the shortest, smallest loan that fits your needs. A 3‑year loan on ₹20,000 might save you thousands compared to a 7‑year term.",
          references: ["psbloansin59minutes.com", "bankrate.com", "livemint.com"]
        },
        {
          title: "2. Skipping Credit Checks Before Applying",
          description: "A low credit score can mean rejection or high interest. Check your credit report beforehand and improve any issues (like overdue payments) to increase approval chances.",
          references: []
        },
        {
          title: "3. Not Shopping Around",
          description: "Rates and fees vary widely. Getting only the first offer could cost you extra. Compared to a 15% offer, an 8% rate saves ₹1,800+ on a ₹15,000 loan over 3 years.",
          references: []
        },
        {
          title: "4. Overlooking Fees & Penalties",
          description: "Check origination fees, processing charges, prepayment penalties, and add‑on services like insurance. Hidden costs can outweigh low interest rates.",
          references: []
        },
        {
          title: "5. Rushing into Instant Loans",
          description: "Emergency loans may come with higher rates and less scrutiny. Even when fast approval is available, take time to review terms and assess EMI affordability.",
          references: []
        },
        {
          title: "6. Submitting Inaccurate or Incomplete Info",
          description: "Mistakes in form fields (name, address, income) lead to delays or rejections. Double-check every detail and upload all necessary documents.",
          references: []
        },
        {
          title: "7. Applying to Too Many Lenders at Once",
          description: "Multiple hard inquiries lower your credit score and signal credit risk. Instead, prequalify with soft inquiries and apply only to top choices.",
          references: []
        },
        {
          title: "8. Ignoring Eligibility Criteria",
          description: "Verify lender requirements for income, age, credit score, and documentation before applying to avoid automatic rejection.",
          references: []
        },
        {
          title: "9. Choosing the Wrong Tenure",
          description: "Shorter tenures mean heftier EMI but lower interest; longer tenures reduce EMI but cost more overall. Align tenure with repayment ability.",
          references: []
        },
        {
          title: "10. No Clear Repayment Plan",
          description: "Before applying, map out EMIs vs income, prioritize loan against other debts, and avoid unnecessary borrowing. A strong repayment plan builds lender confidence.",
          references: []
        },
        {
          title: "✅ Summary: Quick Fix Table",
          description: "",
          table: [
            {
              mistake: "1. Over-borrowing/long tenure",
              impact: "Raises interest costs",
              fix: "Borrow less; choose shortest realistic term"
            },
            {
              mistake: "2. No credit check",
              impact: "Poor score = rejection or high rate",
              fix: "Review and improve credit before applying"
            },
            {
              mistake: "3. No rate comparison",
              impact: "Miss out on better deals",
              fix: "Compare multiple lenders"
            },
            {
              mistake: "4. Ignoring fees",
              impact: "Hidden charges inflate the cost",
              fix: "Read fee schedule carefully"
            },
            {
              mistake: "5. Rushed instant loan",
              impact: "High cost, poor fit",
              fix: "Slow down, review terms, check EMI"
            },
            {
              mistake: "6. Mistakes in application",
              impact: "Leads to rejection/delays",
              fix: "Double-check all entries and uploads"
            },
            {
              mistake: "7. Multiple applications at once",
              impact: "Lowers credit score",
              fix: "Soft prequalification; apply selectively"
            },
            {
              mistake: "8. Not verifying eligibility",
              impact: "Wasted applications",
              fix: "Check minimum criteria before applying"
            },
            {
              mistake: "9. Wrong tenure choice",
              impact: "Increases financial burden",
              fix: "Align tenure with repayment capacity"
            },
            {
              mistake: "10. No repayment plan",
              impact: "Risk of default and stress",
              fix: "Create a repayment schedule matching income"
            }
          ],
          references: []
        }
      ]
    },
    {
      id: 'home-loan-tax-benefits',
      title: "Home Loan Tax Benefits: Complete Guide for 2025",
      excerpt: "Comprehensive guide to all tax benefits available on home loans under various sections.",
      readTime: "8 min read",
      category: "Home Loans",
      content: [
        {
          title: "🏠 Home Loan Tax Benefits — Sections at a Glance",
          description: "A complete breakdown of all tax benefits available for home loan borrowers in 2025.",
          references: []
        },
        {
          title: "1. Section 80C – Principal Repayment & Property Charges",
          description: "Deduction up to ₹1.5 lakh annually on principal repayment, as well as stamp duty and registration fees. Applies only to completed properties—not under construction. If you sell the property within 5 years of possession, deductions previously claimed are revoked.",
          references: ["indiatoday.in", "ujjivansfb.in", "dbs.com"]
        },
        {
          title: "2. Section 24(b) – Interest Paid",
          description: "Up to ₹2 lakh deduction per year for self-occupied homes. No upper limit for let-out properties, but losses from rental income are capped at ₹2 lakh per year and can be carried forward.",
          references: ["aavas.in", "jiraaf.com", "dbs.com"]
        },
        {
          title: "3. Section 80EE & 80EEA – For First-Time & Affordable Home Buyers",
          description: "80EE: Extra ₹50,000 interest deduction (loan ≤ ₹35 lakh; property value ≤ ₹50 lakh). 80EEA: Extra ₹1.5 lakh for affordable housing (loan sanctioned FY 2019–22; stamp duty ≤ ₹45 lakh), available even alongside Sections 24 & 80C. Note: No fresh 80EEA claims for loans sanctioned after March 2022; previous claims (for eligible loans) continue till full repayment.",
          references: ["timesofindia.indiatimes.com", "cleartax.in", "aavas.in"]
        },
        {
          title: "4. Joint Home Loans",
          description: "Co-borrowers/co-owners each get individual deductions: ₹1.5 lakh under Section 80C and ₹2 lakh under Section 24(b). Any 80EE/80EEA benefits apply per borrower.",
          references: ["indiatoday.in", "ltfinance.com", "aavas.in"]
        },
        {
          title: "🆚 Old Regime vs New Regime (Lock-box of 2025 Budget)",
          description: "Old Regime: Allows Sections 80C, 24(b), 80EE/A—creating up to ~₹3.5 lakh in yearly deductions. New Regime (115BAC): Currently excludes all home loan deductions. Choose the Old Regime if you're using home loan tax benefits and your income is above ₹12 lakh—otherwise, New Regime may yield lower tax liability.",
          references: ["indiatoday.in", "dbs.com", "jiraaf.com"]
        },
        {
          title: "📊 Example Calculation – FY 2025‑26",
          description: "Loan Amount: ₹40 lakh | Rate: 8% | Tenure: 20 years\n\nIn Year 1:\n- Interest ≈ ₹3.12 lakh\n- Principal ≈ ₹68,000\n\nDeductions:\n- Interest: ₹2 lakh (Section 24)\n- Principal: ₹68,000 (80C)\n- No 80EE/80EEA (loan beyond their caps)\n→ Total deduction = ₹2.68 lakh",
          references: ["aavas.in", "jiraaf.com", "icicibank.com"]
        },
        {
          title: "💡 Strategy Tips",
          description: "1. Pre-construction interest can be claimed in up to 5 equal yearly installments after completion.\n\n2. Prepay wisely—with the new regime's loss of deductions, paying early reduces overall interest burden.\n\n3. Choose regime smartly: If your total deductions (home loan + others) exceed ₹75,000 and income > ₹12 lakh, the Old Regime may be more tax-efficient.\n\n4. Documentation: For old regime, file lender loan statements, interest certificates, and proof of stamp duty/registration; ITR form now mandates details of lenders & property to claim deductions.",
          references: ["dbs.com", "godrejcapital.com", "indiatoday.in"]
        },
        {
          title: "🧾 Summary Table",
          description: "Quick reference for all home loan tax benefits and their conditions",
          table: [
            {
              mistake: "Section 80C",
              impact: "Principal + stamp/registration",
              fix: "₹1.5 lakh/year; only for completed properties; reversal if sold within 5 years"
            },
            {
              mistake: "24(b)",
              impact: "Interest paid",
              fix: "₹2 lakh/year for self-occupied; no limit for let-out (rental loss cap ₹2 lakh)"
            },
            {
              mistake: "80EE",
              impact: "First-time buyer interest",
              fix: "₹50,000/year; loan ≤ ₹35 lakh, property ≤ ₹50 lakh"
            },
            {
              mistake: "80EEA",
              impact: "Affordable housing interest",
              fix: "₹1.5 lakh/year; for loans sanctioned during FY 2019–22 with stamp duty ≤ ₹45 lakh"
            },
            {
              mistake: "Joint Loans",
              impact: "Per co-borrower benefit",
              fix: "Each can claim 80C & 24(b); 80EE/A apply individually if eligible"
            },
            {
              mistake: "Tax Regime",
              impact: "Old vs New regime",
              fix: "Old supports deductions; New (≥₹12 lakh income) may have lower tax despite no deductions"
            }
          ],
          references: []
        }
      ]
    },
    {
      id: 'business-vs-personal-loan',
      title: "Business Loan vs Personal Loan: Which is Better for Your Business?",
      excerpt: "Detailed comparison to help you choose the right financing option for your business needs.",
      readTime: "6 min read",
      category: "Business Loans",
      content: [
        {
          title: "📌 Business Loan vs Personal Loan: Which Is Better for Your Business?",
          description: "A comprehensive comparison to help you make an informed decision about financing your business.",
          references: []
        },
        {
          title: "1. Purpose & Usage",
          description: "Business Loans are designed exclusively for business needs—expansion, working capital, equipment, inventory, etc. Borrowing personal loans for business can blur finances.\n\nPersonal Loans offer flexibility for any purpose, including business—but using them for business might complicate accounting and limit benefits.",
          references: ["5paisa.com", "indusind.com", "razorpay.com", "investopedia.com", "bajajfinserv.in"]
        },
        {
          title: "2. Loan Amount & Tenure",
          description: "Business Loans typically allow larger amounts (up to ₹5 cr+), with longer tenure (up to 10–15 years).\n\nPersonal Loans are usually capped at ₹40–50 lakh, with shorter tenures (1–6 years).",
          references: ["kotak.com", "airtel.in", "wintwealth.com", "indiatoday.in", "tallycapital.tallysolutions.com"]
        },
        {
          title: "3. Interest Rates & Costs",
          description: "Business Loans often offer lower interest rates (starting ~8% for secured MSME loans) and may include tax-deductible interest costs.\n\nPersonal Loans have higher rates (~10–25%), fixed rate but no tax benefit, and may include higher processing fees and pre-payment penalties.",
          references: ["tallycapital.tallysolutions.com"]
        },
        {
          title: "4. Eligibility & Collateral Requirements",
          description: "Business Loans need proof of business (e.g. financials, turnover, vintage), and often require collateral or personal guarantees.\n\nPersonal Loans are unsecured or minimally secured, requiring only KYC and income proof—simpler and quicker approval.",
          references: ["architecturaldigest.com", "smfgindiacredit.com", "indiatoday.in", "wintwealth.com"]
        },
        {
          title: "5. Credit Mix & Financial Impact",
          description: "Business Loans build business credit, potentially improving future borrowing terms.\n\nPersonal Loans for business may strain personal credit history—missed EMI payments could hurt both personal and business access to finance.",
          references: ["5paisa.com", "smfgindiacredit.com"]
        },
        {
          title: "6. Application Process & Documentation",
          description: "Business Loans involve detailed documentation—business plans, audited financials, bank statements, GST, etc., leading to longer processing times.\n\nPersonal Loans are fast-track approvals (often within 24–48 hrs), with minimal paperwork—ideal for urgent requirements.",
          references: []
        },
        {
          title: "🧭 Comparison Table at a Glance",
          description: "Quick reference for comparing Business Loans and Personal Loans",
          table: [
            {
              mistake: "Feature",
              impact: "Business Loan",
              fix: "Personal Loan"
            },
            {
              mistake: "Use",
              impact: "Business-only",
              fix: "Any purpose (incl. business)"
            },
            {
              mistake: "Loan Amount",
              impact: "High (₹3 L–₹5 Cr+)",
              fix: "Lower (up to ₹40–50 L)"
            },
            {
              mistake: "Tenure",
              impact: "Up to 15 years",
              fix: "1–6 years"
            },
            {
              mistake: "Interest Rate",
              impact: "Lower (~8% secured MSME)",
              fix: "Higher (10–25%)"
            },
            {
              mistake: "Tax Benefits",
              impact: "Yes – interest deductible",
              fix: "No"
            },
            {
              mistake: "Collateral",
              impact: "Often required",
              fix: "Usually unsecured"
            },
            {
              mistake: "Eligibility Docs",
              impact: "Business financials, GST, projections",
              fix: "KYC + income proof"
            },
            {
              mistake: "Processing Time",
              impact: "3–7 days+ (more due diligence)",
              fix: "1–2 days (instant options available)"
            },
            {
              mistake: "Credit Impact",
              impact: "Builds business credit; personal guarantee",
              fix: "Impacts personal credit score"
            }
          ],
          references: []
        },
        {
          title: "👥 Which Works for You?",
          description: "",
          content: [
            {
              title: "✅ Use a Business Loan if:",
              description: "\n• You need large funding (≥₹25 lakh)\n• Want tax-efficient financing\n• Can provide business documentation/collateral\n• You're building business credit",
              references: []
            },
            {
              title: "✅ Opt for a Personal Loan if:",
              description: "\n• You need small, fast funds (<₹5 lakh)\n• Lack business vintage or documents\n• You're okay with higher interest and no tax benefit\n• Simplicity and speed outweigh cost",
              references: []
            }
          ],
          references: []
        }
      ]
    },
    {
      id: 'improve-cibil-score',
      title: "How to Improve Your CIBIL Score in 90 Days",
      excerpt: "Proven strategies and actionable tips to boost your credit score quickly.",
      readTime: "7 min read",
      category: "Credit Score",
      content: [
        {
          title: "🏦 How to Improve Your CIBIL Score in 90 Days",
          description: "A comprehensive 90-day action plan to boost your credit score to 750+",
          references: []
        },
        {
          title: "✅ What is a CIBIL Score?",
          description: "Your CIBIL score is a 3-digit number (300–900) that reflects your creditworthiness. A score above 750 is considered excellent and increases your chances of loan approval and better interest rates.",
          references: []
        },
        {
          title: "🔥 90-Day Action Plan to Boost Your CIBIL Score",
          description: "A step-by-step guide to improve your credit score in 3 months",
          content: [
            {
              title: "Week 1–2: Quick Fixes",
              description: "• Get Your Credit Report: Visit CIBIL or use apps like Paytm, PaisaBazaar, or BankBazaar.\n• Dispute Errors: Raise disputes directly on the CIBIL site (takes 15–30 days to resolve).\n• Pay Off Overdue Amounts: Clear any pending EMIs, credit card dues, or 'written-off' loans if possible.",
              references: []
            },
            {
              title: "Week 3–6: Smart Management",
              description: "• Reduce Credit Utilization: Keep usage below 30% of your total credit limit.\n• Convert Big Dues into EMIs: Use credit card EMI conversion for large balances.\n• Don't Close Old Credit Cards: Older cards build your credit history.",
              references: []
            },
            {
              title: "Week 7–10: Build Positive History",
              description: "• Pay EMIs on Time: Set auto-debit or reminders.\n• Use a Credit Builder Loan or Secured Card: Apply for a secured credit card against FD.\n• Avoid Multiple Loan Applications: Too many hard inquiries reduce your score.",
              references: []
            },
            {
              title: "Week 11–13: Stabilize & Sustain",
              description: "• Keep Credit Mix Healthy: Have a mix of secured and unsecured credit.\n• Monitor Progress: Check your score every 30 days.\n• Most users see improvements of 40–100 points in 3 months.",
              references: []
            }
          ],
          references: []
        },
        {
          title: "📊 Summary Table: 90-Day Credit Boost Plan",
          description: "Quick reference for the 90-day credit improvement plan",
          table: [
            {
              mistake: "Timeframe",
              impact: "Action",
              fix: "Impact on Score"
            },
            {
              mistake: "Week 1–2",
              impact: "Dispute errors, clear overdue",
              fix: "High"
            },
            {
              mistake: "Week 3–6",
              impact: "Lower credit use, convert to EMIs",
              fix: "High"
            },
            {
              mistake: "Week 7–10",
              impact: "Pay EMIs on time, use secured card",
              fix: "Medium–High"
            },
            {
              mistake: "Week 11–13",
              impact: "Maintain credit mix, monitor score",
              fix: "Medium"
            }
          ],
          references: []
        },
        {
          title: "🧠 Bonus Tips",
          description: "• Avoid co-signing for risky borrowers\n• Don't increase card limits too often\n• If you're salaried, link credit card due dates to salary credit dates",
          references: []
        },
        {
          title: "📈 Real Results from Indian Borrowers",
          description: "Inspiring success stories from people who improved their CIBIL scores using these methods",
          content: [
            {
              title: "Ravi's Journey: 645 → 738 in 75 Days",
              description: "Mumbai | Age: 32\n\n• Started with a score of 645\n• Cleared all pending credit card dues\n• Used a secured credit card responsibly\n• Result: 93-point improvement in just 2.5 months",
              references: []
            },
            {
              title: "Anjali's Success: 702 → 790 in 90 Days",
              description: "Bangalore | Age: 28\n\n• Reduced credit card utilization below 30%\n• Paid 2 EMIs in advance\n• Maintained timely payments\n• Result: 88-point boost to excellent score",
              references: []
            }
          ],
          references: []
        }
      ]
    }
  ];

  type ResourceType = 'eligibility-guide' | 'interest-rates' | 'financial-planning' | 'documentation-checklist' | 'credit-score-guide' | 'emi-calculator';

  const handleDownload = async (resourceTitle: string, action: 'download' | 'open' = 'download') => {
    // Map resource titles to their corresponding PDF types with proper typing
    const resourceMap: Record<string, { type: ResourceType; filename: string }> = {
      "Complete Loan Eligibility Guide": {
        type: 'eligibility-guide',
        filename: 'Complete-Loan-Eligibility-Guide.pdf'
      },
      "Interest Rate Comparison Report": {
        type: 'interest-rates',
        filename: 'Interest-Rate-Comparison-Report.pdf'
      },
      "Financial Planning Handbook": {
        type: 'financial-planning',
        filename: 'Financial-Planning-Handbook.pdf'
      },
      "Documentation Checklist": {
        type: 'documentation-checklist',
        filename: 'Documentation-Checklist.pdf'
      },
      "Credit Score Improvement Guide": {
        type: 'credit-score-guide',
        filename: 'Credit-Score-Improvement-Guide.pdf'
      }
    };

    const resource = resourceMap[resourceTitle];
    
    if (!resource) {
      toast({
        title: "Resource Not Available",
        description: "This resource is currently not available for direct download.",
        variant: "destructive",
      });
      return;
    }

    // Show loading state
    const loadingToast = toast({
      title: `Preparing ${resourceTitle}...`,
      description: "Please wait while we generate your document.",
    });

    try {
      // Generate the PDF blob
      const blob = await generatePdfBlob(resource.type);
      
      // Dismiss loading toast
      if (loadingToast) {
        // The toast function returns an object with a dismiss method
        loadingToast.dismiss();
      }
      
      if (blob) {
        if (action === 'download') {
          await downloadPdf(blob, resource.filename);
          toast({
            title: "Download Started!",
            description: `Your ${resourceTitle} is being downloaded.`,
          });
        } else {
          await openPdfInNewTab(blob);
          toast({
            title: "Opening in New Tab",
            description: `The ${resourceTitle} is opening in a new tab.`,
          });
        }
      }
    } catch (error) {
      console.error('Error handling PDF:', error);
      // Dismiss loading toast if it exists
      if (loadingToast) {
        loadingToast.dismiss();
      }
      toast({
        title: "Error",
        description: `There was an error ${action === 'download' ? 'downloading' : 'opening'} the PDF. Please try again.`,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-16 md:pt-20">
      <FloatingButtons />
      {/* Hero Section */}
      <section className="py-8 sm:py-12 md:py-16 px-4">
        <div className="container mx-auto text-center px-2">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6">
            Free Resources
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8 px-2">
            Download our exclusive financial tools, guides, and resources to make informed loan decisions
          </p>
        </div>
      </section>

      {/* Download Resources */}
      <section className="py-8 sm:py-12 md:py-16 px-3 sm:px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-12">Downloadable Resources</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-2 sm:px-0">
            {downloadResources.map((resource, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-[1.02] sm:hover:scale-105">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start mb-3 sm:mb-4">
                    <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg mr-3 sm:mr-4 flex-shrink-0">
                      <resource.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-white mb-1 sm:mb-2 leading-tight">{resource.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-300 mb-2 sm:mb-3 leading-relaxed">{resource.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3 sm:mb-4 text-[10px] sm:text-xs">
                    <div className="bg-blue-500/20 px-1.5 sm:px-2 py-1 rounded text-blue-400 text-center truncate">
                      {resource.type}
                    </div>
                    <div className="bg-green-500/20 px-1.5 sm:px-2 py-1 rounded text-green-400 text-center truncate">
                      {resource.size}
                    </div>
                    {resource.pages && (
                      <div className="bg-purple-500/20 px-1.5 sm:px-2 py-1 rounded text-purple-400 text-center truncate">
                        {resource.pages}
                      </div>
                    )}
                    {resource.features && (
                      <div className="bg-orange-500/20 px-1.5 sm:px-2 py-1 rounded text-orange-400 text-center truncate">
                        {resource.features}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 sm:gap-2.5">
                    <Button 
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white border-blue-700 py-2 text-sm sm:text-base h-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(resource.title, 'download');
                      }}
                    >
                      <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">Download Now</span>
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      className="w-full bg-transparent hover:bg-white/10 text-white border-white/30 py-2 text-sm sm:text-base h-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(resource.title, 'open');
                      }}
                    >
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">Open in New Tab</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Articles */}
      <section className="py-10 sm:py-12 md:py-16 px-3 sm:px-4">
        <div className="container mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-12">Latest Articles</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {blogs.map((blog) => (
              <Card 
                key={blog.id} 
                className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 active:scale-[0.99]"
                onClick={() => handleBlogClick(blog.id)}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex justify-between items-start mb-2 sm:mb-3 gap-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-white leading-tight flex-1 min-w-0">
                      <span className="line-clamp-2">{blog.title}</span>
                    </h3>
                    <div className="text-[10px] sm:text-xs text-blue-400 bg-blue-500/20 px-2 py-1 rounded ml-2 flex-shrink-0 whitespace-nowrap">
                      {blog.category}
                    </div>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-gray-300 mb-3 sm:mb-4 leading-relaxed line-clamp-3">
                    {blog.excerpt}
                  </p>
                  
                  <div className="flex justify-between items-center pt-1">
                    <div className="text-[10px] sm:text-xs text-gray-400">{blog.readTime}</div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-0 h-auto text-xs sm:text-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBlogClick(blog.id);
                      }}
                    >
                      Read More →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Blog Detail View */}
      <Dialog open={isBlogOpen} onOpenChange={setIsBlogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-slate-900 border-slate-700 overflow-hidden">
          {selectedBlog && (
            <>
              <DialogHeader className="border-b border-slate-700 p-6 pb-4">
                <DialogTitle className="text-2xl font-bold text-white">
                  {selectedBlog.title}
                </DialogTitle>
                <div className="flex items-center mt-2">
                  <span className="text-sm text-blue-400 bg-blue-500/20 px-2 py-1 rounded">
                    {selectedBlog.category}
                  </span>
                  <span className="text-sm text-gray-400 ml-3">
                    {selectedBlog.readTime}
                  </span>
                </div>
              </DialogHeader>
              
              <ScrollArea className="h-[70vh] px-6 py-4">
                {selectedBlog.content ? (
                  <div className="prose prose-invert max-w-none">
                    {selectedBlog.content.map((section, index) => (
                      <div key={index} className="mb-6">
                        <h3 className="text-xl font-semibold text-white mb-3">
                          {section.title}
                        </h3>
                        {section.content && Array.isArray(section.content) ? (
                          <div className="space-y-6">
                            {section.content?.map((item: BlogContentItem, itemIndex: number) => (
                              <div key={itemIndex} className="bg-slate-800/30 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold text-white mb-2">{item.title}</h4>
                                <p className="text-gray-300 whitespace-pre-line">{item.description}</p>
                              </div>
                            ))}
                          </div>
                        ) : section.table ? (
                          <div className="bg-slate-800/50 p-4 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm text-gray-300">
                              <thead>
                                <tr className="border-b border-slate-700">
                                  <th className="text-left py-2 px-3 font-semibold">Mistake</th>
                                  <th className="text-left py-2 px-3 font-semibold">Why It Matters</th>
                                  <th className="text-left py-2 px-3 font-semibold">Fix</th>
                                </tr>
                              </thead>
                              <tbody>
                                {section.table.map((row, rowIndex) => (
                                  <tr key={rowIndex} className="border-b border-slate-700 last:border-0 hover:bg-slate-700/30 transition-colors">
                                    <td className="py-3 px-3 align-top">{row.mistake}</td>
                                    <td className="py-3 px-3 align-top">{row.impact}</td>
                                    <td className="py-3 px-3 align-top">{row.fix}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : section.title.includes('Table') ? (
                          <div className="bg-slate-800/50 p-4 rounded-lg overflow-x-auto">
                            <pre className="text-sm text-gray-300 whitespace-pre-line">
                              {section.description}
                            </pre>
                          </div>
                        ) : (
                          <p className="text-gray-300 mb-3 leading-relaxed">
                            {section.description}
                          </p>
                        )}
                        {section.references && section.references.length > 0 && (
                          <div className="text-sm text-gray-400 mt-2">
                            <span className="font-medium">References: </span>
                            {section.references.map((ref, i) => (
                              <span key={i}>
                                {i > 0 && ', '}
                                <a 
                                  href={`https://${ref}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:underline"
                                >
                                  {ref}
                                </a>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Content not available.</p>
                  </div>
                )}
              </ScrollArea>
              
              <div className="border-t border-slate-700 p-4 flex justify-between items-center">
                <Button 
                  variant="outline" 
                  className="border-slate-600 text-white hover:bg-slate-800"
                  onClick={closeBlog}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Articles
                </Button>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => {
                      // Handle share functionality
                      toast({
                        title: "Share",
                        description: "Share link copied to clipboard!",
                      });
                      navigator.clipboard.writeText(window.location.href);
                    }}
                  >
                    Share
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Resources;
